import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Loader2, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  "Anniversaire",
  "Mariage",
  "Festival",
  "École",
  "Centre de loisirs",
  "Comité des fêtes",
  "Entreprise",
  "Carnaval",
  "Marché de Noël",
  "Arbre de Noël",
  "Autre",
];

const emptyForm = {
  full_name: "",
  email: "",
  phone: "",
  event_type: "",
  event_date: "",
  city: "",
  participants: "",
  message: "",
  consent: false,
};

export default function QuoteForm() {
  const [values, setValues] = useState(emptyForm);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (k) => (e) => {
    const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setValues((s) => ({ ...s, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    if (!values.consent) {
      toast.error("Veuillez accepter la politique de confidentialité.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/quote-request`, values, { timeout: 30000 });
      setStatus("success");
      setValues(emptyForm);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Une erreur est survenue. Merci de réessayer.";
      setErrorMsg(typeof msg === "string" ? msg : "Une erreur est survenue.");
      setStatus("error");
      toast.error("L'envoi a échoué. Merci de réessayer.");
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-neutral-300 focus:border-neutral-900 focus:outline-none py-3 text-base placeholder:text-neutral-400 transition-colors";

  return (
    <div className="relative">
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div
            key="success"
            data-testid="quote-success"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-3xl bg-white border border-neutral-200 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.15)] p-10 md:p-14 text-center"
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
              <Check className="w-7 h-7 text-emerald-600" strokeWidth={2.5} />
            </div>
            <p className="font-hand text-3xl md:text-4xl text-neutral-900 mb-3">Merci&nbsp;!</p>
            <h3 className="font-serif text-3xl md:text-4xl text-neutral-900 mb-4">
              Merci pour votre demande.
            </h3>
            <p className="text-neutral-600 text-lg leading-relaxed max-w-md mx-auto">
              Je vous répondrai dans les meilleurs délais avec une proposition adaptée à votre événement.
            </p>
            <button
              type="button"
              data-testid="quote-reset"
              onClick={() => setStatus("idle")}
              className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-neutral-900 underline underline-offset-4"
            >
              Envoyer une autre demande
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            data-testid="quote-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl bg-white border border-neutral-200 shadow-[0_30px_60px_-40px_rgba(0,0,0,0.18)] p-8 md:p-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <label className="block">
                <span className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">Nom et prénom *</span>
                <input required data-testid="q-full_name" className={inputCls} value={values.full_name} onChange={update("full_name")} placeholder="Marie Dupont" />
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">Email *</span>
                <input required type="email" data-testid="q-email" className={inputCls} value={values.email} onChange={update("email")} placeholder="vous@exemple.fr" />
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">Téléphone *</span>
                <input required type="tel" data-testid="q-phone" className={inputCls} value={values.phone} onChange={update("phone")} placeholder="06 12 34 56 78" />
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">Type d&apos;événement *</span>
                <select required data-testid="q-event_type" className={inputCls + " appearance-none pr-6 cursor-pointer"} value={values.event_type} onChange={update("event_type")}>
                  <option value="" disabled>Choisir…</option>
                  {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">Date de l&apos;événement *</span>
                <input required type="date" data-testid="q-event_date" className={inputCls} value={values.event_date} onChange={update("event_date")} />
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">Ville / commune *</span>
                <input required data-testid="q-city" className={inputCls} value={values.city} onChange={update("city")} placeholder="Orléans, Loiret" />
              </label>
              <label className="block md:col-span-2">
                <span className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">Nombre approximatif de participants *</span>
                <input required data-testid="q-participants" className={inputCls} value={values.participants} onChange={update("participants")} placeholder="Ex. 15 enfants" />
              </label>
              <label className="block md:col-span-2">
                <span className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">Message / précisions</span>
                <textarea data-testid="q-message" rows={4} className={inputCls + " resize-none py-3"} value={values.message} onChange={update("message")} placeholder="Parlez-moi de votre projet, du lieu, de l'ambiance souhaitée…" />
              </label>
            </div>

            <label className="mt-8 flex items-start gap-3 cursor-pointer">
              <input
                required
                type="checkbox"
                data-testid="q-consent"
                checked={values.consent}
                onChange={update("consent")}
                className="mt-1 w-4 h-4 rounded border-neutral-400 accent-neutral-900"
              />
              <span className="text-sm text-neutral-600 leading-relaxed">
                J&apos;accepte que mes informations soient utilisées uniquement pour répondre à ma demande, conformément à la politique de confidentialité. *
              </span>
            </label>

            {status === "error" && (
              <div className="mt-6 flex items-start gap-3 rounded-2xl bg-red-50 border border-red-100 p-4 text-sm text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p>{errorMsg}</p>
              </div>
            )}

            <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-neutral-500">
                * champs obligatoires
              </p>
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="q-submit"
                className="group inline-flex items-center gap-3 rounded-full text-white font-semibold px-7 py-3.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: "#D48699" }}
                onMouseEnter={(e) => { if (status !== 'loading') e.currentTarget.style.background = "#BC6B80"; }}
                onMouseLeave={(e) => { if (status !== 'loading') e.currentTarget.style.background = "#D48699"; }}
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Envoi en cours…
                  </>
                ) : (
                  <>
                    Envoyer ma demande
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
